import AdvancedController from "./advanced-controller";
import { Breakpoints } from "../utils/breakpoints";
import { debounce } from "../utils/utils";
import Hunt from "huntjs";
import Swiper, { Navigation, Pagination, Autoplay } from "swiper";

const AUTO_HEIGHT_BREAKPOINT = "xxl";

export default class SwiperController extends AdvancedController {
  static targets = ["swiper"];

  static values = {
    autoplay: Boolean,
    sameheight: Boolean,
    onlyautoheightmobile: Boolean,
    maxslides: Boolean
  };

  _slidesPerView;
  _autoplay;
  _autoplayDelay;
  _loop;
  _totalSlides;
  _slidesCloned;
  _spaceBetween;

  connect() {
    super.connect();

    this.childPagesSlider = document.querySelector('.child-pages-slider');

    this._slidesPerView = this.swiperTarget.getAttribute("data-slides-per-view") || 1;
    this._autoplay = !!+this.swiperTarget.getAttribute("data-autoplay-enable");
    this._autoplayDelay = this.swiperTarget.getAttribute("data-autoplay-delay");
    this._loop = !!+this.swiperTarget.getAttribute("data-loop-enable");
    this._spaceBetween = this.swiperTarget.getAttribute("data-space-between");
    this._totalSlides = this.swiperTarget.querySelectorAll(".swiper-slide");
    this._slidesCloned = false;
    this.createSlider(this.swiperTarget, !this.sameheightValue);
    this.breakpoints = new Breakpoints();

    //disable autoslide on start, as it gets handled by huntjs if in viewport
    if (this.hasSwiperTarget && this.onlyautoheightmobileValue) {
      this.manageAutoHeight();
      this.setNavigationHeight();
      window.addEventListener("resize", () =>
        debounce(this.managaAutoHeightAndCheckNavigation(), 250)
      );
    }

    this.disableAutoswipe();
    new Hunt(this.element, {
      persist: true,
      enter: () => this.enableAutoswipe(),
      leave: () => this.disableAutoswipe(),
    });
  }

  enableAutoswipe() {
    // class instead of prop needs to be checked because autoplay prop is also on non-autoplay instances
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      !this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.start();
    }
  }

  disableAutoswipe() {
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      this.swiperTarget.swiper &&
      this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.stop();
    }
  }

  managaAutoHeightAndCheckNavigation() {
    this.manageAutoHeight();
    this.setNavigationHeightToFirstSlide();
  }

  manageAutoHeight() {
    if (
      this.hasSwiperTarget &&
      !this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(true);
    }
    this.setNavigationHeight(this.swiperTarget.swiper.params.autoHeight)
  }

  destroyAndReinit(autoHeight) {
    this.swiperTarget.swiper.destroy();
    this.createSlider(this.swiperTarget, autoHeight);
  }

  createSlider(element, autoHeight = false) {
    const config = {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: this._slidesPerView,
      pagination: {
        el: element.querySelector('.swiper-pagination'),
        bulletActiveClass: 'current',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + "</span>";
        },
      },
      grabCursor: true,
      navigation: {
        prevEl: element.querySelector(".swiper-button-prev"),
        nextEl: element.querySelector(".swiper-button-next"),
      },
    };


    if (this._autoplay) {
      config.autoplay = {
        delay: Number(this._autoplayDelay),
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      };
    }

    if (this._spaceBetween === "") {
      config.spaceBetween = 24; // --tc-gutter-x
    }

    if (this._slidesPerView > 1) {
      const swiperBreakpoints = this.calculateSwiperBreakpoints(this._slidesPerView);
      config.breakpoints = swiperBreakpoints;
    }

    if (this._loop) {
      config.loop = true;
    }


    if (autoHeight) {
      config.autoHeight = true;
    }

    new Swiper(element, config);
  }

  calculateSwiperBreakpoints(slidesPerView) {
    // this will be returned
    const swiperBreakpoints = {};
    // retrieve all breakpoints, remove the largest (xl)
    const breakpointsService = new Breakpoints();
    const allBreakpoints = breakpointsService.getAll()
    delete allBreakpoints.xl;

    // calculate the step size between each breakpoint, e.g. 8 / 4 = 2 (=> xs: 1, sm: 1+2, md: 1+2+2)
    const perBreakpoint = slidesPerView / (Object.keys(allBreakpoints).length + 1)

    // sort the breakpoints by pixel values for the loop
    const breakpointPixelValues = Object.values(allBreakpoints).sort((a, b) => a - b)

    let i = 0;
    // force add a breakpoint for 0px
    swiperBreakpoints[0] = { slidesPerView: 1 };
    for (const breakpointSize of breakpointPixelValues) {
      i++;
      // calculate how much slides shall be displayed
      const slidesPerBreakpoint = Math.round(1 + i * perBreakpoint)
      // add the config
      swiperBreakpoints[breakpointSize] = { slidesPerView: slidesPerBreakpoint }
    }

    return swiperBreakpoints;
  }


  slideTo(index) {
    if (this.hasSwiperTarget) {
      this.swiperTarget.swiper.slideTo(index);
    }
  }


  onChildNavigationSwipe() {
    if (this.childPagesSlider) {
      setTimeout(() => {
        this.getAllControllers("child-page-navigation")[0].scrollToSection(
          this.swiperTarget.swiper.activeIndex
        )
      }, 1)
    }
  }

  setNavigationHeight() {
    if (this.swiperTarget.swiper.params.autoHeight) {
      const nav = this.swiperTarget.parentNode.classList.contains("navigation-same-height");
      if(nav) {
        const prev = getComputedStyle(this.swiperTarget.querySelector(".swiper-button-prev"))
        this.swiperTarget.querySelector(".swiper-button-prev").style.top = `${prev.top}`;
        this.swiperTarget.querySelector(".swiper-button-next").style.top = `${prev.top}`;
      }
    }
  }

  setNavigationHeightToFirstSlide() {
    if (this.swiperTarget.swiper.params.autoHeight) {
      const nav = this.swiperTarget.parentNode.classList.contains("navigation-same-height");
      if(nav) {
        const firstSlide = getComputedStyle(this.swiperTarget.querySelector(".swiper-wrapper .swiper-slide:first-child"))
        const half = parseInt(firstSlide.height, 10) / 2 - 18;
        this.swiperTarget.querySelector(".swiper-button-prev").style.top = `${half}px`;
        this.swiperTarget.querySelector(".swiper-button-next").style.top = `${half}px`;
      }
    }
  }
}
